import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import moment from "moment";
import { useSnackbar } from "notistack";
import integrationKeyServices from "app/services/integration-key-services";

// Configuración → API: las llaves con que el ERP o el partner de la empresa entra a la API de
// integraciones (/api/integrations/v1). El token se ve una sola vez, al crearla o rotarla; en la
// base queda solo su huella, así que una llave perdida se rota o se revoca.

const GRACE_OPTIONS = [
    { value: 0, label: "Ninguna: la actual deja de funcionar ya" },
    { value: 1, label: "1 hora" },
    { value: 24, label: "24 horas" },
    { value: 72, label: "72 horas" },
];

const fmt = (value) => (value ? moment(value).format("DD-MM-YYYY HH:mm") : null);

const KeyStatus = ({ apiKey }) => {
    if (!apiKey.active) return <Chip size="small" label="Revocada" />;
    if (apiKey.revoked_at) {
        return (
            <Tooltip title={`Deja de funcionar el ${fmt(apiKey.revoked_at)}`}>
                <Chip size="small" color="warning" label={`Vence ${moment(apiKey.revoked_at).locale("es").fromNow()}`} />
            </Tooltip>
        );
    }
    return <Chip size="small" color="success" variant="outlined" label="Activa" />;
};

const IntegrationKeysPanel = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [info, setInfo] = useState({ data: [] });
    const [busy, setBusy] = useState(false);

    const [createOpen, setCreateOpen] = useState(false);
    const [newName, setNewName] = useState("");
    const [rotating, setRotating] = useState(null);
    const [grace, setGrace] = useState(24);
    const [revoking, setRevoking] = useState(null);
    // { token, name, previous } recién creado o rotado: se muestra una vez.
    const [revealed, setRevealed] = useState(null);

    const load = useCallback(async () => {
        try {
            setInfo(await integrationKeyServices.list());
        } catch (e) {
            // el interceptor ya mostró el error
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const copy = async (value) => {
        try {
            await navigator.clipboard.writeText(value);
            enqueueSnackbar("Copiado", { variant: "success" });
        } catch (e) {
            enqueueSnackbar("No se pudo copiar: selecciona el texto y copia con Ctrl+C", { variant: "info" });
        }
    };

    const handleCreate = async () => {
        setBusy(true);
        try {
            const res = await integrationKeyServices.create(newName.trim());
            setCreateOpen(false);
            setNewName("");
            setRevealed({ token: res.token, name: res.data.name });
            load();
        } catch (e) {
            // el interceptor ya mostró el error
        } finally {
            setBusy(false);
        }
    };

    const handleRotate = async () => {
        setBusy(true);
        try {
            const res = await integrationKeyServices.rotate(rotating.id, grace);
            setRotating(null);
            setRevealed({ token: res.token, name: res.data.name, previous: res.previous });
            load();
        } catch (e) {
            // el interceptor ya mostró el error
        } finally {
            setBusy(false);
        }
    };

    const handleRevoke = async () => {
        setBusy(true);
        try {
            await integrationKeyServices.revoke(revoking.id);
            enqueueSnackbar("Llave revocada", { variant: "success" });
            setRevoking(null);
            load();
        } catch (e) {
            // el interceptor ya mostró el error
        } finally {
            setBusy(false);
        }
    };

    const keys = info.data || [];

    return (
        <Stack spacing={2}>
            <Card>
                <CardContent>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between" alignItems={{ sm: "flex-start" }}>
                        <Box>
                            <Typography variant="h5" sx={{ mb: 0.5 }}>API de integraciones</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Para que el ERP o el sistema de tu empresa mande pedidos a Haulers y consulte su estado.
                            </Typography>
                            {info.api_base_url && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, wordBreak: "break-all" }}>
                                    Base: <code>{info.api_base_url}</code> · Autenticación: header <code>Authorization: Bearer &lt;token&gt;</code>
                                </Typography>
                            )}
                        </Box>
                        {info.docs_url && (
                            <Button variant="outlined" size="small" startIcon={<OpenInNewOutlinedIcon />}
                                    href={info.docs_url} target="_blank" rel="noopener noreferrer" sx={{ flexShrink: 0 }}>
                                Documentación
                            </Button>
                        )}
                    </Stack>
                </CardContent>
            </Card>

            <Card>
                <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <VpnKeyOutlinedIcon fontSize="small" />
                        <Typography variant="h6" sx={{ flexGrow: 1, mb: 0 }}>Llaves</Typography>
                        <Button variant="contained" size="small" startIcon={<AddOutlinedIcon />} onClick={() => setCreateOpen(true)}>
                            Crear llave
                        </Button>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Trátalas como una contraseña: no las compartas por correo ni las publiques. Usa una llave por
                        sistema o proveedor, así puedes cortar a uno sin afectar al resto.
                    </Typography>

                    {loading ? (
                        <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={24} /></Box>
                    ) : keys.length === 0 ? (
                        <Alert severity="info">Todavía no hay llaves. Crea una para el sistema que se va a conectar.</Alert>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Nombre</TableCell>
                                        <TableCell>Llave</TableCell>
                                        <TableCell>Creada</TableCell>
                                        <TableCell>Último uso</TableCell>
                                        <TableCell>Estado</TableCell>
                                        <TableCell align="right" />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {keys.map((k) => (
                                        <TableRow key={k.id} sx={{ opacity: k.active ? 1 : 0.6 }}>
                                            <TableCell>{k.name}</TableCell>
                                            <TableCell><code>{k.token_prefix}…</code></TableCell>
                                            <TableCell>{fmt(k.created_at)}</TableCell>
                                            <TableCell>{fmt(k.last_used_at) || <Typography variant="body2" color="text.secondary">Nunca usada</Typography>}</TableCell>
                                            <TableCell><KeyStatus apiKey={k} /></TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                                {k.active && (
                                                    <>
                                                        <Tooltip title="Crear una llave nueva que la reemplace">
                                                            <IconButton size="small" onClick={() => { setGrace(24); setRotating(k); }}>
                                                                <AutorenewOutlinedIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Button size="small" color="error" onClick={() => setRevoking(k)}>Revocar</Button>
                                                    </>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            <Dialog open={createOpen} onClose={() => !busy && setCreateOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Crear llave</DialogTitle>
                <DialogContent>
                    <TextField autoFocus fullWidth margin="dense" label="Nombre" value={newName}
                               onChange={(e) => setNewName(e.target.value)}
                               inputProps={{ maxLength: 80 }}
                               helperText="Quién la va a usar, por ejemplo: ERP - proveedor de TI"
                               onKeyDown={(e) => { if (e.key === "Enter" && newName.trim()) handleCreate(); }} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)} disabled={busy}>Cancelar</Button>
                    <LoadingButton variant="contained" loading={busy} disabled={!newName.trim()} onClick={handleCreate}>Crear</LoadingButton>
                </DialogActions>
            </Dialog>

            <Dialog open={!!rotating} onClose={() => !busy && setRotating(null)} fullWidth maxWidth="sm">
                <DialogTitle>Rotar «{rotating?.name}»</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Se crea una llave nueva con el mismo nombre. Para que el sistema que la usa no se corte mientras
                        la cambian, la llave actual puede seguir funcionando un tiempo.
                    </Typography>
                    <TextField select fullWidth label="La llave actual sigue funcionando" value={grace}
                               onChange={(e) => setGrace(Number(e.target.value))}>
                        {GRACE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRotating(null)} disabled={busy}>Cancelar</Button>
                    <LoadingButton variant="contained" loading={busy} onClick={handleRotate}>Rotar</LoadingButton>
                </DialogActions>
            </Dialog>

            <Dialog open={!!revoking} onClose={() => !busy && setRevoking(null)} fullWidth maxWidth="xs">
                <DialogTitle>Revocar «{revoking?.name}»</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        El sistema que la usa deja de poder entrar en este momento. No se puede deshacer: para volver a
                        conectarlo hay que crear otra llave.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRevoking(null)} disabled={busy}>Cancelar</Button>
                    <LoadingButton variant="contained" color="error" loading={busy} onClick={handleRevoke}>Revocar</LoadingButton>
                </DialogActions>
            </Dialog>

            <Dialog open={!!revealed} fullWidth maxWidth="sm">
                <DialogTitle>Llave «{revealed?.name}»</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Cópiala ahora y guárdala en un lugar seguro: no se vuelve a mostrar.
                    </Alert>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField fullWidth value={revealed?.token || ""} InputProps={{ readOnly: true, sx: { fontFamily: "monospace" } }}
                                   onFocus={(e) => e.target.select()} />
                        <IconButton onClick={() => copy(revealed?.token)}><ContentCopyOutlinedIcon /></IconButton>
                    </Stack>
                    {revealed?.previous?.active && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            La llave anterior ({revealed.previous.token_prefix}…) sigue funcionando hasta el {fmt(revealed.previous.revoked_at)}.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => setRevealed(null)}>Ya la guardé</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default IntegrationKeysPanel;
